/**
 * Reference models for user-management-lib
 * 
 * @remarks
 * These lightweight reference models are used when entities reference
 * other entities. They provide just enough information for display
 * without requiring full entity details.
 * 
 * Reference models use a union type pattern to support both:
 * - Unpopulated references (just the ID as a string)
 * - Populated references (full object with details)
 */

/**
 * Minimal user reference
 * 
 * @remarks
 * Used when entities need to reference a user without including
 * the full user object. Common in author, creator, and member fields.
 */
export interface UserRef {
  /**
   * User unique identifier
   */
  _id: string;

  /**
   * User's display name
   */
  name: string;

  /**
   * User's email address
   */
  email: string;

  /**
   * Optional user profile photo URL
   */
  photoURL?: string;
}

/**
 * Minimal group reference used in user profile responses
 * 
 * @remarks
 * Returned as part of organization membership summaries in
 * GET /api/users/me, GET /api/auth/profile, and GET /api/users/profile/detailed.
 */
export interface GroupSummary {
  /** Group unique identifier */
  _id: string;
  /** Group display name */
  name: string;
}

/**
 * Minimal organization reference
 * 
 * @remarks
 * Used when entities belong to or reference an organization.
 */
export interface OrganizationRef {
  /**
   * Organization unique identifier
   */
  _id: string;

  /**
   * Organization display name
   */
  name: string;

  /**
   * Unique organization code for lookups and invitations
   */
  orgCode: string;

  /**
   * Role the current user holds within this organization.
   * Populated by GET /api/users/me and GET /api/auth/profile.
   * Optional for backward compatibility with older API responses.
   */
  userRole?: OrganizationRole;

  /**
   * Groups the current user belongs to within this organization.
   * Populated by GET /api/users/me, GET /api/auth/profile, and
   * GET /api/users/profile/detailed.
   * Optional for backward compatibility with older API responses.
   */
  groups?: GroupSummary[];
}

/**
 * Organization role enumeration
 * 
 * @remarks
 * Defines the three levels of access within an organization:
 * - Admin: Full control over organization and members
 * - Editor: Can create and edit content
 * - Reader: Read-only access
 */
export type OrganizationRole = 'Admin' | 'Editor' | 'Reader';

/**
 * Organization member with unpopulated user reference
 * 
 * @remarks
 * Used in organization member arrays when user details are not populated.
 * The user field contains just the user ID string.
 */
export interface OrganizationMember {
  /**
   * User ID reference
   */
  user: string;

  /**
   * User's role within the organization
   */
  role: OrganizationRole;
}

/**
 * Organization member with populated user reference
 * 
 * @remarks
 * Used in organization member arrays when user details are populated.
 * The user field contains the full UserRef object.
 */
export interface OrganizationMemberPopulated {
  /**
   * Full user reference with details
   */
  user: UserRef;

  /**
   * User's role within the organization
   */
  role: OrganizationRole;
}
